import React from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/layout/Layout';
import { SeoHead } from '../components/ui/SeoHead';
import { Section } from '../components/ui/Section';
import { Map, ArrowRight } from 'lucide-react';

const siteLinks = [
  {
    category: 'Main Pages',
    links: [
      { name: 'Home', path: '/', description: 'Company overview, capabilities summary, and operational indicators' },
      { name: 'Capabilities', path: '/services', description: 'Field services, asset support, and logistics capabilities' },
      { name: 'Projects & Experience', path: '/projects', description: 'Execution history across GCC and MENA operations' },
      { name: 'HSE & Safety', path: '/safety', description: 'Health, safety, and environmental management systems' },
      { name: 'About Us', path: '/about', description: 'Corporate profile, mission, vision, and leadership' },
      { name: 'Contact', path: '/contact', description: 'Commercial and operational inquiry form' },
      { name: 'Tender Support', path: '/tender-support', description: 'Engineering-driven tender response and technical capability' },
    ],
  },
  {
    category: 'Service Areas',
    links: [
      { name: 'Well Testing', path: '/services/well-testing', description: 'Surface well testing and flowback operations' },
      { name: 'Pressure Pumping', path: '/services/pressure-pumping', description: 'High-pressure pumping and well stimulation' },
      { name: 'Equipment Rental', path: '/services/equipment', description: 'Oilfield equipment rental and asset management' },
      { name: 'Fabrication & Maintenance', path: '/services/fabrication', description: 'Skid packages, valve maintenance, and NDT' },
      { name: 'Logistics & Inventory', path: '/services/logistics', description: 'Equipment mobilization and supply chain coordination' },
    ],
  },
  {
    category: 'Legal',
    links: [
      { name: 'Privacy Policy', path: '/privacy', description: 'Data collection, usage, and protection practices' },
      { name: 'Terms of Service', path: '/terms', description: 'Website usage terms and conditions' },
      { name: 'Sitemap', path: '/sitemap', description: 'Complete website navigation map' },
    ],
  },
  {
    category: 'Documents',
    links: [
      { name: 'Capability Statement (PDF)', path: '/files/energipro-capability-statement.pdf', description: 'Downloadable corporate capability statement', external: true },
    ],
  },
];

export const Sitemap = () => {
  return (
    <Layout>
      <SeoHead
        title="Sitemap | EnergiPro Solutions"
        description="Complete sitemap of EnergiPro Solutions website: services, projects, safety, about, contact, tender support, and company pages."
        canonicalPath="/sitemap"
      />
      <div className="bg-slate-900 pt-80 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4 mb-6">
            <Map className="text-orange-600" size={48} />
            <h1 className="text-4xl md:text-5xl font-bold text-white">SITEMAP</h1>
          </div>
          <p className="text-xl text-slate-300 max-w-3xl leading-relaxed">
            Complete navigation guide for the EnergiPro Solutions website.
          </p>
        </div>
      </div>

      <Section>
        <div className="max-w-4xl mx-auto">
          <div className="space-y-12">
            {siteLinks.map((section) => (
              <div key={section.category}>
                <h2 className="text-xl font-bold text-slate-900 mb-6 pb-3 border-b-2 border-orange-600 inline-block">
                  {section.category}
                </h2>
                <div className="space-y-4">
                  {section.links.map((link) =>
                    link.external ? (
                      <a
                        key={link.path}
                        href={link.path}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-4 p-4 bg-slate-50 hover:bg-orange-50 border border-slate-200 hover:border-orange-200 rounded-sm transition-all group"
                      >
                        <ArrowRight size={18} className="text-orange-600 shrink-0 group-hover:translate-x-1 transition-transform" />
                        <div>
                          <span className="font-bold text-slate-900 group-hover:text-orange-600 transition-colors">
                            {link.name}
                          </span>
                          <p className="text-sm text-slate-500 mt-0.5">{link.description}</p>
                        </div>
                      </a>
                    ) : (
                      <Link
                        key={link.path}
                        to={link.path}
                        className="flex items-center gap-4 p-4 bg-slate-50 hover:bg-orange-50 border border-slate-200 hover:border-orange-200 rounded-sm transition-all group"
                      >
                        <ArrowRight size={18} className="text-orange-600 shrink-0 group-hover:translate-x-1 transition-transform" />
                        <div>
                          <span className="font-bold text-slate-900 group-hover:text-orange-600 transition-colors">
                            {link.name}
                          </span>
                          <p className="text-sm text-slate-500 mt-0.5">{link.description}</p>
                        </div>
                      </Link>
                    )
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </Section>
    </Layout>
  );
};
